<mat-toolbar
  id="main-nav"
  tabindex="-1"
  [dir]="direction"
  class="nav-bar"
  >
  <div class="bar">
    <!-- Burger only in DOM on &lt;1440px — no stray focus on desktop -->
    @if (!isDesktopNavVisible) {
      <button
        class="burger"
        type="button"
        [attr.aria-label]="openSideMenuAriaLabel"
        (click)="toggleSidebar()"
        >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
    }

    <!-- Logo (h1 — primary page heading for the route planner) -->
    <h1 class="logo-heading">
      <a class="logo" routerLink="/" [attr.aria-label]="logoAltText">
        <img src="/assets/images/logo.svg" [alt]="logoAltText" (click)="navigateToWebsite()"/>
      </a>
    </h1>

    <!-- Desktop navigation only in DOM on ≥1440px -->
    @if (isDesktopNavVisible) {
      <nav
        class="main-nav"
        aria-label="primary"
        >
        @for (menuItem of menuItems; track menuItem; let i = $index) {
          <div
            class="nav-item"
            [class.has-dropdown]="menuItem.children?.length"
            [class.dropdown-open]="isDropdownOpen(i)">
            <!--
              Disclosure (collapse/expand) button that toggles a submenu.
              Per audit (a11y): MUST be a real <button> with aria-expanded so
              SR announces "collapsed/expanded". The CDK overlay origin still
              works on a <button> element.
            -->
            @if (menuItem.children?.length) {
              <button
                type="button"
                class="nav-link"
                [id]="'main-nav-trigger-' + i"
                [attr.aria-expanded]="isDropdownOpen(i) ? 'true' : 'false'"
                aria-haspopup="true"
                [attr.aria-controls]="'main-nav-submenu-' + i"
                cdkOverlayOrigin
                #menuTrigger="cdkOverlayOrigin"
                (click)="toggleDropdown(i, $event, menuTrigger)">
                {{ menuItem.title }}
                <svg class="dropdown-icon" width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                  <path d="M8.07422 9.4375L7.35547 8.71875L1.35547 2.71875L0.636719 2L2.07422 0.59375L2.76172 1.3125L8.07422 6.59375L13.3555 1.3125L14.0742 0.59375L15.4805 2L14.7617 2.71875L8.76172 8.71875L8.07422 9.4375Z" fill="#0C3058"/>
                </svg>
              </button>
            }
            <!--
              Leaf item (no children): real <a [href]> opening the CMS page in
              a new tab. As an <a> with href it's keyboard-focusable and SR
              announces it as a link; aria-label appends "opens in a new tab".
            -->
            @if (!menuItem.children?.length) {
              <a
                [href]="menuItemHref(menuItem.url)"
                target="_blank"
                rel="noopener noreferrer"
                [attr.aria-label]="menuLeafAriaLabel(menuItem.title)"
                class="nav-link">
                {{ menuItem.title }}
              </a>
            }
            <!--
              Submenu rendered via CDK overlay so it sits above the map.
              The wrapping div is the popup the trigger's aria-controls points
              to; aria-labelledby ties it back to the trigger's accessible name.
            -->
            @if (menuItem.children?.length) {
              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="activeMenuTrigger"
                [cdkConnectedOverlayOpen]="isDropdownOpen(i)"
                [cdkConnectedOverlayHasBackdrop]="true"
                cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                (backdropClick)="openDropdownIndex = null"
                [cdkConnectedOverlayPositions]="menuDropdownPositions"
                [cdkConnectedOverlayOffsetY]="8">
                <div
                  class="nav-menu-overlay"
                  role="group"
                  [id]="'main-nav-submenu-' + i"
                  [attr.aria-labelledby]="'main-nav-trigger-' + i"
                  [attr.aria-label]="submenuAriaLabel(menuItem.title)">
                  @for (child of menuItem.children; track child) {
                    <a
                      [href]="menuItemHref(child.url)"
                      target="_blank"
                      rel="noopener noreferrer"
                      [attr.aria-label]="menuLeafAriaLabel(child.title)"
                      (click)="openDropdownIndex = null"
                      class="nav-menu-item">{{ child.title }}</a>
                  }
                </div>
              </ng-template>
            }
          </div>
        }
      </nav>
    }

    <!-- Right cluster: search flush next to language (single flex group + margin-inline-start: auto) -->
    <div class="actions-group">
      <button
        type="button"
        class="icon search"
        [attr.aria-label]="searchAriaLabel"
        [attr.aria-expanded]="isSearchOpen"
        [attr.aria-controls]="isSearchOpen ? 'header-search-panel' : null"
        (click)="openSearch($event)"
        >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" fill="none"/>
          <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" stroke-width="2"/>
        </svg>
      </button>

      <!--
        Language selector — disclosure button + menu group.
        a11y: aria-haspopup="menu" + aria-expanded + descriptive aria-label
        ("Choose site language. Current: {x}") so the disclosure state and
        purpose are announced before activation. Matches mot-website-client.
      -->
      <div class="lang">
        <button class="lang-btn" type="button"
          cdkOverlayOrigin #langTrigger="cdkOverlayOrigin"
          (click)="toggleLang()"
          [attr.aria-expanded]="langOpen ? 'true' : 'false'"
          [attr.aria-haspopup]="'menu'"
          [attr.aria-controls]="langOpen ? 'header-lang-group' : null"
          [attr.aria-label]="languageMenuButtonAriaLabel">
          <span aria-hidden="true">{{ currentLangLabel }}</span>
          <svg class="caret" viewBox="0 0 20 20" aria-hidden="true">
            <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2"/>
          </svg>
        </button>

        <ng-template cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="langTrigger"
          [cdkConnectedOverlayOpen]="langOpen"
          [cdkConnectedOverlayHasBackdrop]="true"
          cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
          (backdropClick)="langOpen = false"
          [cdkConnectedOverlayPositions]="langOverlayPositions"
          [cdkConnectedOverlayOffsetY]="8">
          <div
            class="lang-menu-overlay"
            id="header-lang-group"
            role="group"
            [attr.aria-label]="chooseLanguageText">
            @for (l of languages; track l) {
              <button
                type="button"
                class="lang-item"
                [class.active]="l.code === currentLang"
                [attr.aria-pressed]="l.code === currentLang ? 'true' : 'false'"
                [attr.aria-label]="langOptionAriaLabel(l)"
                (click)="selectLang(l.code)"
                >
                {{ l.label }}
              </button>
            }
          </div>
        </ng-template>
      </div>

      <!--
        Contact button — opens a dialog. a11y: aria-haspopup="dialog" +
        aria-expanded so SR announces "collapsed/expanded" disclosure state.
      -->
      <mot-button
        [text]="contactUsText"
        buttonClass="contact-btn"
        [ariaHaspopup]="'dialog'"
        [ariaExpanded]="isContactOpen"
        (onClick)="openContact()"
        />
        <mot-button
          [text]="submitRequestText"
          buttonClass="cta-btn"
          (onClick)="handleSubmitRequestClick()"
          />
        </div>
      </div>

      <!-- Sidebar Menu -->
      <app-side-menu
        [isOpen]="isSidebarOpen"
        (close)="closeSidebar()"
        (openContact)="openContact()">
      </app-side-menu>
    </mat-toolbar>

    <app-contact-view
      [isOpen]="isContactOpen"
      [contactInfo]="contactInfo"
      (close)="closeContact()">
    </app-contact-view>

    <!--
    Outside mat-toolbar: MDC toolbar clips overflow (dropdown). Markup/classes match mot-website-client header.
    -->
    @if (isSearchOpen) {
      <div
        class="search-overlay"
        id="header-search-panel"
        role="search"
        [attr.aria-expanded]="isSearchOpen"
        (click)="closeSearch()"
        >
        <div
          class="search-container"
          [class.expanded]="isSearchOpen"
          [attr.dir]="direction === 'rtl' ? 'rtl' : 'ltr'"
          (click)="$event.stopPropagation()"
          [ngStyle]="searchInputStyle"
          >
          <div class="search-bar">
            <svg class="search-icon-left" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" fill="none"/>
              <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" stroke-width="2"/>
            </svg>
            <input
              type="text"
              class="search-input"
              [value]="searchQuery"
              (input)="onSearchQueryInput($event)"
              (keydown.enter)="onSearchEnter($event)"
              [attr.placeholder]="searchAriaLabel"
              [attr.aria-label]="searchAriaLabel"
              aria-autocomplete="list"
              autocomplete="off"
              />
            </div>
            @if (searchQuery.trim().length >= 2 && (searchResults$ | async); as results) {
              <div
                class="search-results"
                role="list"
                [attr.aria-expanded]="true"
                [attr.aria-label]="searchAriaLabel"
                >
                @if (results.length > 0) {
                  @for (result of results; track result) {
                    <button
                      type="button"
                      class="search-result-item"
                      role="listitem"
                      (click)="navigateToSearchResult(result)"
                      >
                      {{ result.title }}
                    </button>
                  }
                }
                @if (results.length === 0) {
                  <div class="search-no-results">
                    {{ searchNoResultsText }}
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
